import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Rate, Image, Badge, Spin, Card, Button, Typography, Alert, Divider, Tag, Space, Dropdown, Menu } from 'antd';
import {
  ArrowLeftOutlined,
  ShoppingCartOutlined,
  HeartOutlined,
  HeartFilled,
  EnvironmentOutlined,
  PhoneOutlined,
  StarOutlined,
  ShareAltOutlined,
  FacebookOutlined,
  TwitterOutlined,
  LinkedinOutlined,
  WhatsAppOutlined,
  CopyOutlined
} from '@ant-design/icons';
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  WhatsappIcon
} from 'react-share';
import { useCart } from '../../contexts/CartContext';
import { useWishlist } from '../../contexts/WishlistContext';
import api from '../../services/api';
import {
  getDietaryTagColor,
  PLACEHOLDER_IMG,
  safeImageOnError
} from '../../utils/ui';
import { getImageForDetail, getAllImages } from '../../utils/images';
import AIInfoButton from '../UI/AIInfoButton';
import ImageCarousel from '../UI/ImageCarousel';
import './menudetail.css';

const { Title, Paragraph, Text } = Typography;

function MenuItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist, wishlistLoading } = useWishlist();

  const [menuItem, setMenuItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMenuItem();
  }, [id]);

  const loadMenuItem = async () => {
    try {
      setLoading(true);
      const resp = await api.getMenu(id);
      if (resp.success) {
        setMenuItem(resp.data);
      } else {
        setError('Menu item not found');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load menu item');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (menuItem) addToCart(menuItem, 1);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      // You could add a toast notification here
      console.log('Link copied to clipboard');
    } catch (err) {
      console.log('Failed to copy link:', err);
    }
  };

  const getShareMenu = () => {
    if (!menuItem) return null;
    
    const shareUrl = window.location.href;
    const shareTitle = menuItem.name;
    const shareDescription = `Check out this delicious ${menuItem.name} from ${menuItem.vendor?.business_name}`;

    return (
      <Menu className="rounded-xl shadow-lg border-0">
        <Menu.Item key="facebook" className="px-4 py-3">
          <FacebookShareButton
            url={shareUrl}
            quote={shareDescription}
            className="flex items-center w-full"
          >
            <div className="flex items-center space-x-3">
              <FacebookIcon size={24} round />
              <span className="text-gray-700">Facebook</span>
            </div>
          </FacebookShareButton>
        </Menu.Item>
        
        <Menu.Item key="twitter" className="px-4 py-3">
          <TwitterShareButton
            url={shareUrl}
            title={shareDescription}
            className="flex items-center w-full"
          >
            <div className="flex items-center space-x-3">
              <TwitterIcon size={24} round />
              <span className="text-gray-700">Twitter</span>
            </div>
          </TwitterShareButton>
        </Menu.Item>
        
        <Menu.Item key="whatsapp" className="px-4 py-3">
          <WhatsappShareButton
            url={shareUrl}
            title={shareDescription}
            className="flex items-center w-full"
          >
            <div className="flex items-center space-x-3">
              <WhatsappIcon size={24} round />
              <span className="text-gray-700">WhatsApp</span>
            </div>
          </WhatsappShareButton>
        </Menu.Item>
        
        <Menu.Item key="linkedin" className="px-4 py-3">
          <LinkedinShareButton
            url={shareUrl}
            title={shareTitle}
            summary={shareDescription}
            className="flex items-center w-full"
          >
            <div className="flex items-center space-x-3">
              <LinkedinIcon size={24} round />
              <span className="text-gray-700">LinkedIn</span>
            </div>
          </LinkedinShareButton>
        </Menu.Item>
        
        <Menu.Divider />
        
        <Menu.Item key="copy" className="px-4 py-3" onClick={handleCopyLink}>
          <div className="flex items-center space-x-3">
            <CopyOutlined className="text-gray-600" />
            <span className="text-gray-700">Copy Link</span>
          </div>
        </Menu.Item>
      </Menu>
    );
  };

  const handleToggleWishlist = () => {
    if (menuItem) toggleWishlist(menuItem, 'menu');
  };

  const renderDietaryBadges = () => {
    const badges = [];
    if (menuItem.is_vegetarian) badges.push(
      <Tag key="veg" color="green" className="px-3 py-1 rounded-full text-sm font-medium shadow-sm">
        🥬 Veg
      </Tag>
    );
    if (menuItem.is_vegan) badges.push(
      <Tag key="vegan" color="blue" className="px-3 py-1 rounded-full text-sm font-medium shadow-sm">
        🌱 Vegan
      </Tag>
    );
    if (menuItem.is_gluten_free) badges.push(
      <Tag key="gf" color="purple" className="px-3 py-1 rounded-full text-sm font-medium shadow-sm">
        🌾 Gluten-Free
      </Tag>
    );
    if (menuItem.is_dairy_free) badges.push(
      <Tag key="df" color="magenta" className="px-3 py-1 rounded-full text-sm font-medium shadow-sm">
        🥛 Dairy-Free
      </Tag>
    );
    if (menuItem.is_spicy) badges.push(
      <Tag key="spicy" color="red" className="px-3 py-1 rounded-full text-sm font-medium shadow-sm">
        🌶️ Spicy
      </Tag>
    );
    return <div className="flex flex-wrap gap-2">{badges}</div>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }
  if (error || !menuItem) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card>
          <Alert
            message="Menu Item Not Found"
            description={error || "The menu item doesn't exist."}
            type="error"
            showIcon
            action={<Button onClick={() => navigate('/menu')}>Back to Menu</Button>}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Modern Header */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                type="text"
                size="large"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(-1)}
                className="hover:bg-gray-100 rounded-full"
              />
              <div>
                <h1 className="text-lg font-semibold text-gray-900 truncate max-w-xs">
                  {menuItem.name}
                </h1>
                <p className="text-sm text-gray-500">
                  {menuItem.vendor?.business_name}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <AIInfoButton
                menuItem={menuItem}
                type="primary"
                size="large"
                variant="gradient"
                className="px-6 py-2 text-base font-semibold rounded-lg"
              >
                Ask AI
              </AIInfoButton>
              <Dropdown overlay={getShareMenu()} trigger={['click']} placement="bottomRight">
                <Button
                  type="text"
                  size="large"
                  icon={<ShareAltOutlined />}
                  className="hover:bg-gray-100 rounded-full"
                />
              </Dropdown>
              <Button
                type="text"
                size="large"
                loading={wishlistLoading[`menu_${menuItem.id}`]}
                onClick={handleToggleWishlist}
                className={`rounded-full ${
                  isInWishlist(menuItem.id, 'menu') 
                    ? 'bg-red-50 text-red-500 hover:bg-red-100' 
                    : 'hover:bg-gray-100'
                }`}
                icon={
                  isInWishlist(menuItem.id, 'menu') ?
                    <HeartFilled /> :
                    <HeartOutlined />
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Image */}
          <div className="space-y-6">
            {/* Hero Image */}
            <div className="relative group">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl" style={{ backgroundColor: '#000000' }}>
                {(() => {
                  const allImages = getAllImages(menuItem, 'large');
                  console.log('MenuItemDetail Debug:', {
                    menuItemName: menuItem.name,
                    allImages: allImages,
                    hasMultipleImages: allImages.length > 1,
                    imagesArray: menuItem.images
                  });
                  return allImages.length > 1;
                })() ? (
                  <ImageCarousel
                    item={menuItem}
                    size="large"
                    height="100%"
                    showIndicators={true}
                    showArrows={true}
                    showPreview={true}
                    autoplay={true}
                    maxImages={5}
                    className="w-full h-full"
                  />
                ) : (
                  <Image
                    src={getImageForDetail(menuItem)}
                    alt={menuItem.name}
                    fallback={PLACEHOLDER_IMG}
                    onError={safeImageOnError}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    style={{ backgroundColor: '#000000' }}
                  />
                )}
              </div>
              
              {/* Floating Badges */}
              <div className="absolute top-6 left-6 flex flex-wrap gap-2">
                {renderDietaryBadges()}
              </div>
              
              {/* Rating Badge */}
              {menuItem.rating && (
                <div className="absolute top-6 right-6">
                  <div className="bg-white/90 backdrop-blur-sm rounded-full px-3 py-2 flex items-center space-x-1 shadow-lg">
                    <StarOutlined className="text-yellow-500" />
                    <span className="font-semibold text-gray-900">
                      {Number(menuItem.rating).toFixed(1)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Vendor Info Card */}
            <Card className="rounded-2xl shadow-sm border-0 bg-white/80 backdrop-blur-sm">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">
                    {menuItem.vendor?.business_name?.charAt(0)}
                  </span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {menuItem.vendor?.business_name}
                  </h3>
                  <div className="flex items-center space-x-4 text-gray-600">
                    <div className="flex items-center space-x-1">
                      <EnvironmentOutlined className="text-sm" />
                      <span className="text-sm">{menuItem.vendor?.city?.name}</span>
                    </div>
                    {menuItem.vendor?.phone && (
                      <div className="flex items-center space-x-1">
                        <PhoneOutlined className="text-sm" />
                        <span className="text-sm">{menuItem.vendor.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Right Column - Content */}
          <div className="space-y-6">
            {/* Title and Rating */}
            <div className="space-y-4">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2 leading-tight">
                  {menuItem.name}
                </h1>
                {menuItem.rating && (
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="flex items-center space-x-1">
                      <Rate disabled value={menuItem.rating} className="text-lg" />
                      <span className="text-gray-600 font-medium">
                        {Number(menuItem.rating).toFixed(1)}
                      </span>
                    </div>
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-600">
                      {menuItem.total_reviews} reviews
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            {menuItem.description && (
              <Card className="rounded-2xl shadow-sm border-0 bg-white/80 backdrop-blur-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">About this dish</h3>
                <p className="text-gray-700 leading-relaxed">{menuItem.description}</p>
              </Card>
            )}

            {/* Cuisine Tags */}
            {menuItem.cuisine_tags?.length > 0 && (
              <Card className="rounded-2xl shadow-sm border-0 bg-white/80 backdrop-blur-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Cuisine & Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {menuItem.cuisine_tags.map((tag, idx) => (
                    <Tag
                      key={idx}
                      color={getDietaryTagColor(tag)}
                      className="px-3 py-1 rounded-full text-sm font-medium"
                    >
                      {tag}
                    </Tag>
                  ))}
                </div>
              </Card>
            )}


            {/* Action Buttons */}
            <div className="action-buttons-container" style={{
              display: 'grid',
              justifyItems: 'center',
              alignItems: 'end',
              justifyContent: 'center',
              alignContent: 'space-evenly',
              gap: '12px'
            }}>
              <Button
                type="primary"
                size="large"
                icon={<ShoppingCartOutlined />}
                onClick={handleAddToCart}
                className="w-full h-14 rounded-2xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 border-0 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                style={{ gridColumn: '1 / -1' }}
              >
                Add to Cart
              </Button>
              
              <div className="secondary-actions-grid" style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                width: '100%',
                justifyItems: 'center',
                alignItems: 'end'
              }}>
                <Button
                  size="large"
                  icon={
                    isInWishlist(menuItem.id, 'menu') ?
                      <HeartFilled className="text-red-500" /> :
                      <HeartOutlined />
                  }
                  onClick={handleToggleWishlist}
                  loading={wishlistLoading[`menu_${menuItem.id}`]}
                  className="h-12 rounded-xl border border-gray-200 hover:border-red-300 hover:text-red-500 w-full"
                >
                  {isInWishlist(menuItem.id, 'menu') ? 'Saved' : 'Save'}
                </Button>
                <Dropdown overlay={getShareMenu()} trigger={['click']} placement="topRight">
                  <Button
                    size="large"
                    icon={<ShareAltOutlined />}
                    className="h-12 rounded-xl border border-gray-200 hover:border-blue-300 hover:text-blue-500 w-full"
                  >
                    Share
                  </Button>
                </Dropdown>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MenuItemDetail;
